import React from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { keyframes } from "@emotion/react";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import LocalPrintshopOutlinedIcon from "@mui/icons-material/LocalPrintshopOutlined";
import NorthEastRoundedIcon from "@mui/icons-material/NorthEastRounded";

const motionSafe = {
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
};

const rise = keyframes`
  from { opacity: 0; transform: translateY(10px) scale(0.97); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

const floatMail = keyframes`
  0%, 100% { transform: translateY(0) rotate(-4deg); }
  50% { transform: translateY(-5px) rotate(-2deg); }
`;

const floatPrint = keyframes`
  0%, 100% { transform: translateY(0) rotate(3deg); }
  50% { transform: translateY(-4px) rotate(5deg); }
`;

const pulseDot = keyframes`
  0%, 100% { opacity: 0.35; transform: scale(0.9); }
  50% { opacity: 1; transform: scale(1.05); }
`;

function ChoicePreview({ mailAccent, printAccent }) {
  return (
    <Box
      aria-hidden
      sx={{
        position: "relative",
        height: 88,
        mt: 1.35,
        borderRadius: 2,
        overflow: "hidden",
        bgcolor: alpha(mailAccent, 0.06),
        border: "1px solid",
        borderColor: alpha(mailAccent, 0.18),
        animation: `${rise} 320ms ease both`,
        ...motionSafe,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          left: "18%",
          top: 16,
          width: 52,
          height: 52,
          borderRadius: 1.5,
          bgcolor: alpha(mailAccent, 0.14),
          border: "1.5px solid",
          borderColor: alpha(mailAccent, 0.45),
          display: "grid",
          placeItems: "center",
          boxShadow: `0 8px 18px ${alpha(mailAccent, 0.2)}`,
          animation: `${floatMail} 2.6s ease-in-out infinite`,
          ...motionSafe,
        }}
      >
        <MailOutlineRoundedIcon sx={{ fontSize: 26, color: mailAccent }} />
      </Box>
      <Box
        sx={{
          position: "absolute",
          left: "50%",
          top: 38,
          width: 10,
          height: 10,
          ml: "-5px",
          borderRadius: "50%",
          bgcolor: alpha(mailAccent, 0.55),
          animation: `${pulseDot} 1.8s ease-in-out infinite`,
          ...motionSafe,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          right: "18%",
          top: 16,
          width: 52,
          height: 52,
          borderRadius: 1.5,
          bgcolor: alpha(printAccent, 0.14),
          border: "1.5px solid",
          borderColor: alpha(printAccent, 0.45),
          display: "grid",
          placeItems: "center",
          boxShadow: `0 8px 18px ${alpha(printAccent, 0.2)}`,
          animation: `${floatPrint} 2.6s ease-in-out infinite`,
          animationDelay: "0.15s",
          ...motionSafe,
        }}
      >
        <LocalPrintshopOutlinedIcon sx={{ fontSize: 26, color: printAccent }} />
      </Box>
    </Box>
  );
}

function ChoiceCard({
  accent,
  icon,
  title,
  body,
  onClick,
  disabled,
  primary = false,
}) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      disabled={disabled}
      sx={{
        all: "unset",
        boxSizing: "border-box",
        cursor: disabled ? "default" : "pointer",
        flex: 1,
        minWidth: 0,
        p: 1.5,
        borderRadius: 2,
        border: "1.5px solid",
        borderColor: primary ? alpha(accent, 0.55) : alpha(accent, 0.28),
        bgcolor: primary ? alpha(accent, 0.1) : alpha(accent, 0.04),
        transition:
          "transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease",
        opacity: disabled ? 0.55 : 1,
        animation: `${rise} 360ms ease both`,
        ...motionSafe,
        "&:hover:not(:disabled)": {
          transform: "translateY(-2px)",
          borderColor: accent,
          boxShadow: `0 10px 22px ${alpha(accent, 0.22)}`,
        },
        "&:focus-visible": {
          outline: `2px solid ${accent}`,
          outlineOffset: 2,
        },
      }}
    >
      <Stack spacing={1}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 1.5,
            display: "grid",
            placeItems: "center",
            bgcolor: alpha(accent, 0.16),
            color: accent,
          }}
        >
          {icon}
        </Box>
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 800, lineHeight: 1.2, color: "text.primary" }}
        >
          {title}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ lineHeight: 1.4, fontWeight: 600 }}
        >
          {body}
        </Typography>
      </Stack>
    </Box>
  );
}

/**
 * Antes de ofertar: elegir email o imprimir PDF (estilo Meetings).
 */
export default function EstimateOfertarChoiceDialog({
  open,
  onClose,
  onChooseEmail,
  onChoosePrint,
  busy = false,
  alreadyOffered = false,
}) {
  const theme = useTheme();
  const mailAccent = theme.palette.primary.main || "#1976d2";
  const printAccent = theme.palette.success.main || "#2e7d32";

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      fullWidth
      maxWidth="xs"
      keepMounted={false}
      transitionDuration={160}
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: "blur(10px)",
            backgroundColor: "rgba(15, 23, 42, 0.45)",
          },
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          border: "1px solid",
          borderColor: "divider",
        },
      }}
    >
      <Box
        sx={{
          px: 2.5,
          pt: 2,
          pb: 1.5,
          borderBottom: "1px solid",
          borderColor: "divider",
          background: (th) =>
            `linear-gradient(135deg, ${alpha(th.palette.primary.main, 0.12)} 0%, ${alpha(
              th.palette.success.main,
              0.08,
            )} 100%)`,
        }}
      >
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          spacing={1}
        >
          <Stack
            direction="row"
            spacing={1.25}
            alignItems="center"
            sx={{ minWidth: 0 }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1.5,
                display: "grid",
                placeItems: "center",
                bgcolor: alpha(mailAccent, 0.16),
                color: mailAccent,
                flexShrink: 0,
              }}
            >
              <NorthEastRoundedIcon sx={{ fontSize: 22 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 800, lineHeight: 1.15 }}
              >
                Ofertar presupuesto
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", lineHeight: 1.4, mt: 0.3 }}
              >
                Elige cómo presentarlo al cliente
              </Typography>
            </Box>
          </Stack>
          <IconButton
            aria-label="Cerrar"
            onClick={onClose}
            disabled={busy}
            size="small"
          >
            <CloseOutlinedIcon />
          </IconButton>
        </Stack>
        <ChoicePreview mailAccent={mailAccent} printAccent={printAccent} />
      </Box>

      <Box sx={{ px: 2.5, pt: 2, pb: 1 }}>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            lineHeight: 1.5,
            fontWeight: 600,
            mb: 1.5,
            animation: `${rise} 300ms ease both`,
            ...motionSafe,
          }}
        >
          {alreadyOffered
            ? "Puedes reenviar por email o volver a descargar el PDF."
            : "Enviar email sigue el circuito habitual. Imprimir descarga el PDF y deja el presupuesto en Offered."}
        </Typography>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.25}
          sx={{ mb: 0.5 }}
        >
          <ChoiceCard
            accent={mailAccent}
            primary
            disabled={busy}
            onClick={onChooseEmail}
            icon={<MailOutlineRoundedIcon sx={{ fontSize: 22 }} />}
            title="Enviar email"
            body="Abre el editor de oferta Unibox y marca Offered si el envío OK."
          />
          <ChoiceCard
            accent={printAccent}
            disabled={busy}
            onClick={onChoosePrint}
            icon={
              busy ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <LocalPrintshopOutlinedIcon sx={{ fontSize: 22 }} />
              )
            }
            title={busy ? "Preparando…" : "Imprimir PDF"}
            body={
              alreadyOffered
                ? "Descarga el presupuesto en PDF sin enviar email."
                : "Descarga el PDF y marca el presupuesto como Offered."
            }
          />
        </Stack>
      </Box>

      <Stack
        direction="row"
        spacing={1.25}
        justifyContent="flex-end"
        sx={{
          px: 2.5,
          py: 1.75,
          borderTop: "1px solid",
          borderColor: "divider",
          bgcolor: (th) => alpha(th.palette.background.default, 0.65),
        }}
      >
        <Button
          onClick={onClose}
          disabled={busy}
          sx={{ fontWeight: 700, minHeight: 44 }}
        >
          Cancelar
        </Button>
      </Stack>
    </Dialog>
  );
}
